import type { FC } from "react";

import { buildUri, sizes } from "@ui/components/ResponsiveImage/uriTemplate";
import { getBuilderImageResizeUrl } from "@ui/utils/getBuilderImageResizeUrl";

import { ChevronForwardIcon } from "@jti/brand-icons/chevron-forward";
import { ImageBehaviour } from "@jti/core-components/components/ImageBehaviour";
import { LinkBehaviour } from "@jti/core-components/components/LinkBehaviour";

import styles from "./LinkCard.module.scss";

const LINK_CARD_LG_IMAGE_SIZE = 92;

type ImageType = {
  src: string;
  title: string;
  alt: string;
};

export type LinkCardProps = {
  image: ImageType;
  link: string;
  label: string;
  altText?: string;
  openIn?: "_blank" | "";
  dataTestId?: string;
  onClick: (label: string) => void;
};

export const LinkCard: FC<LinkCardProps> = ({ link, openIn, label, dataTestId, image, altText, onClick }) => {
  const handleClick = () => {
    return onClick(label);
  };

  const imageSrc = buildUri(image.src, sizes.small);
  const optimizedImageSrc =
    getBuilderImageResizeUrl({
      imageUrl: imageSrc,
      width: LINK_CARD_LG_IMAGE_SIZE * 2,
      height: LINK_CARD_LG_IMAGE_SIZE * 2,
    }) || imageSrc;

  return (
    <LinkBehaviour
      href={link}
      target={openIn}
      rel={openIn === "_blank" ? "noreferrer" : ""}
      className={styles.linkCard}
      data-testid={dataTestId}
      onClick={handleClick}
    >
      <div className={styles.linkCardImageWrapper}>
        <ImageBehaviour
          data-testid="link-card-image"
          src={optimizedImageSrc}
          className={styles.linkCardImage}
          alt={altText || image.alt}
        />
      </div>
      <div className={styles.contentWrapper}>
        <div className={styles.linkCardLabel} data-testid="link-card-label">
          {label}
        </div>
        <div data-testid="link-card-chevron-icon" className={styles.linkCardChevron}>
          <ChevronForwardIcon />
        </div>
      </div>
    </LinkBehaviour>
  );
};
